import { Time } from "@internationalized/date";

type Hour = `${number}${number}`;
type Minute = `${number}${number}`;
type Second = `${number}${number}`;
export type TimeOnly = `${Hour}:${Minute}` | `${Hour}:${Minute}:${Second}`;

export const formatTimeOnly = (
	timeStr: TimeOnly | string | null | undefined,
) => {
	if (!timeStr) return null;

	const [hours, minutes] = timeStr.split(":").map(Number);

	if (hours === undefined || minutes === undefined) return null;

	const period = hours >= 12 ? "PM" : "AM";
	const hour12 = hours % 12 || 12;

	return `${hour12}:${minutes.toString().padStart(2, "0")} ${period}`;
};

/**
 * Checks if the time portion of a given date is before the specified time-only value
 * @param date - The date to check (Date object)
 * @param timeOnly - The time to compare against in HH:MM or HH:MM:SS format
 * @returns true if the date's time is before the timeOnly, false otherwise
 */
export const isTimeOnlyBefore = (
	date: Date,
	timeOnly: TimeOnly | string,
): boolean => {
	const targetTime = timeOnlyToTime(timeOnly);
	if (!targetTime) return false;

	const dateTime = new Time(
		date.getHours(),
		date.getMinutes(),
		date.getSeconds(),
	);

	return dateTime.compare(targetTime) < 0;
};

/**
 * Returns the difference between a date's time and a time-only value as an object with hours, minutes, and seconds
 * @param date - The date to check (Date object)
 * @param timeOnly - The time to compare against in HH:MM or HH:MM:SS format
 * @returns an object with hours, minutes, and seconds difference (positive if date time is after timeOnly, negative if before)
 */
export const getTimeOnlyDifferenceInSeconds = (
	date: Date,
	timeOnly: TimeOnly | string,
): { hours: number; minutes: number; seconds: number } => {
	const targetTime = timeOnlyToTime(timeOnly);
	if (!targetTime) return { hours: 0, minutes: 0, seconds: 0 };

	const dateTime = new Time(
		date.getHours(),
		date.getMinutes(),
		date.getSeconds(),
	);

	// Calculate the total difference in seconds
	const dateSeconds =
		dateTime.hour * 3600 + dateTime.minute * 60 + dateTime.second;
	const targetSeconds =
		targetTime.hour * 3600 + targetTime.minute * 60 + targetTime.second;

	const totalDifferenceSeconds = dateSeconds - targetSeconds;

	// Convert back to hours, minutes, seconds
	const isNegative = totalDifferenceSeconds > 0;
	const absDifference = Math.abs(totalDifferenceSeconds);

	const hours = Math.floor(absDifference / 3600);
	const minutes = Math.floor((absDifference % 3600) / 60);
	const seconds = absDifference % 60;
	console.log(isNegative, hours, minutes, seconds);
	return {
		hours: isNegative ? -hours : hours,
		minutes: isNegative ? -minutes : minutes,
		seconds: isNegative ? -seconds : seconds,
	};
};

/**
 * Converts a TimeOnly string to a Time object from @internationalized/date
 * @param timeOnly - The time string in HH:MM or HH:MM:SS format
 * @returns Time object or null if invalid
 */
export const timeOnlyToTime = (
	timeOnly: TimeOnly | string | null | undefined,
): Time | null => {
	if (!timeOnly) return null;

	const [hours, minutes, seconds = 0] = timeOnly.split(":").map(Number);

	if (
		hours === undefined ||
		minutes === undefined ||
		hours < 0 ||
		hours > 23 ||
		minutes < 0 ||
		minutes > 59 ||
		seconds < 0 ||
		seconds > 59
	) {
		return null;
	}

	return new Time(hours, minutes, seconds);
};

/**
 * Compares the time portion of a date with a time-only value using Time.compare()
 * @param date - The date to check (Date object)
 * @param timeOnly - The time to compare against in HH:MM or HH:MM:SS format
 * @returns negative if date time is before timeOnly, positive if after, 0 if equal
 */
export const compareTimeOnly = (
	date: Date,
	timeOnly: TimeOnly | string,
): number => {
	const targetTime = timeOnlyToTime(timeOnly);
	if (!targetTime) return 0;

	const dateTime = new Time(
		date.getHours(),
		date.getMinutes(),
		date.getSeconds(),
	);
	return dateTime.compare(targetTime);
};

/**
 * Calculates the difference between two TimeOnly values
 * @param timeOnly1 - The first time in HH:MM or HH:MM:SS format
 * @param timeOnly2 - The second time in HH:MM or HH:MM:SS format
 * @returns an object with hours, minutes, and seconds difference (timeOnly1 - timeOnly2)
 */
export const getTimeOnlyDifference = (
	timeOnly1: TimeOnly | string,
	timeOnly2: TimeOnly | string,
): { hours: number; minutes: number; seconds: number } => {
	const time1 = timeOnlyToTime(timeOnly1);
	const time2 = timeOnlyToTime(timeOnly2);

	if (!time1 || !time2) return { hours: 0, minutes: 0, seconds: 0 };

	// Calculate the total difference in seconds
	const time1Seconds = time1.hour * 3600 + time1.minute * 60 + time1.second;
	const time2Seconds = time2.hour * 3600 + time2.minute * 60 + time2.second;

	const totalDifferenceSeconds = time1Seconds - time2Seconds;

	// Convert back to hours, minutes, seconds
	const isNegative = totalDifferenceSeconds < 0;
	const absDifference = Math.abs(totalDifferenceSeconds);

	const hours = Math.floor(absDifference / 3600);
	const minutes = Math.floor((absDifference % 3600) / 60);
	const seconds = absDifference % 60;

	return {
		hours: isNegative ? -hours : hours,
		minutes: isNegative ? -minutes : minutes,
		seconds: isNegative ? -seconds : seconds,
	};
};
